import type { AuditLogController_listQueryParams } from '@/services/types';
import { AuditLogActorType, AuditLogMethod, AuditLogOutcome } from '@/services/types';

import { normalizeAuditLogFilters } from './filterRules';
import { storedRoutePrefix } from './route';
import type { AuditActorType, AuditLogFilters, AuditMethod, AuditOutcome } from './types';

export type AuditLogQueryParams = Omit<AuditLogController_listQueryParams, 'cursor' | 'limit'>;

// The generated client types enum params as TS enums, which a string literal does not
// satisfy, so each list goes through its own table.
const ACTOR_TYPE_PARAM: Record<AuditActorType, AuditLogActorType> = {
    user: AuditLogActorType.user,
    api_user: AuditLogActorType.api_user,
    production_key: AuditLogActorType.production_key,
    anonymous: AuditLogActorType.anonymous,
};

const OUTCOME_PARAM: Record<AuditOutcome, AuditLogOutcome> = {
    success: AuditLogOutcome.success,
    denied: AuditLogOutcome.denied,
    failed: AuditLogOutcome.failed,
};

const METHOD_PARAM: Record<AuditMethod, AuditLogMethod> = {
    GET: AuditLogMethod.GET,
    POST: AuditLogMethod.POST,
    PUT: AuditLogMethod.PUT,
    PATCH: AuditLogMethod.PATCH,
    DELETE: AuditLogMethod.DELETE,
};

/**
 * The list and the export both send this, so a CSV holds exactly the entries the page
 * shows, audit log views left out whenever the page hides them.
 */
export function toAuditLogQueryParams(filters: AuditLogFilters): AuditLogQueryParams {
    const { from, to, actorType, outcome, method, route, ...rest } =
        normalizeAuditLogFilters(filters);
    const params: AuditLogQueryParams = { ...rest };
    if (from !== undefined) params.from = from.toISOString();
    if (to !== undefined) params.to = to.toISOString();
    if (actorType !== undefined) params.actorType = actorType.map((type) => ACTOR_TYPE_PARAM[type]);
    if (outcome !== undefined) params.outcome = outcome.map((value) => OUTCOME_PARAM[value]);
    if (method !== undefined) params.method = method.map((value) => METHOD_PARAM[value]);
    // The table shows routes without their API prefix, so the filter is typed that way.
    if (route !== undefined) params.route = storedRoutePrefix(route);
    return params;
}
